import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Heart } from "lucide-react";
import HeroSlideshow from "@/components/HeroSlideshow";
import DailySpiritualHub from "@/components/DailySpiritualHub";
import MassTimes from "@/components/MassTimes";
import TitheOffertory from "@/components/TitheOffertory";
import { AnnouncementsHomeRow } from "@/pages/Announcements";
import { fetchSlides } from "@/lib/api";
import { useContent } from "@/lib/ContentContext";
import { TIDS } from "@/constants/testIds";
import { ASSETS } from "@/lib/constants";
import SEO from "@/components/SEO";

/*
 * Bundled fallback hero slides, used when the admin has uploaded no slides
 * yet or when the API is temporarily unreachable. Kept in sync with the
 * privacy deletions from Delivery 6 (no tabernacle / eucharist / choir
 * instruments). Every path here must resolve inside /images/parish/.
 */
const FALLBACK_HERO_SLIDES = [
    { src: "/images/slides/slide-01-octagonal-interior.png",   alt: "View of the church interior displaying its unique octagonal ceiling and seating layout" },
    { src: "/images/slides/slide-02-blessed-virgin-mary.jpg",  alt: "Statue of the Blessed Virgin Mary adorned with fresh roses" },
    { src: "/images/slides/slide-03-sunday-worship.jpg",       alt: "Parishioners gathered in prayer during Sunday worship inside the sanctuary" },
    { src: "/images/slides/slide-04-tabernacle-divine-mercy.jpg", alt: "The Tabernacle niche alongside artwork of the Divine Mercy and the Resurrection" },
    { src: "/images/slides/slide-05-img4780.webp",             alt: "Holy Trinity Kileleshwa, a moment of parish life" },
    { src: ASSETS.sanctuary,       alt: "Inside the sanctuary at Holy Trinity Kileleshwa" },
    { src: ASSETS.massCelebration, alt: "The Holy Mass celebrated with reverence and joy" },
    { src: ASSETS.procession,      alt: "Children's procession on a Sunday" },
    { src: ASSETS.marianShrine,    alt: "Marian shrine in the parish grounds" },
    { src: ASSETS.holyWaterFont,   alt: "Holy water font at the church entrance" },
    { src: ASSETS.congregation,    alt: "Holy Trinity Kileleshwa congregation at worship" },
    { src: ASSETS.steeple,         alt: "Cross atop Holy Trinity Kileleshwa steeple" },
];

/*
 * Homepage — streamlined per parish feedback (Delivery 6):
 *   - Hero (no Live Stream button, no "Kileleshwa, Archdiocese of Nairobi" eyebrow)
 *   - Daily Spiritual Hub (Verse + Saint auto-refresh; Readings link goes internal)
 *   - Mass Times (with anchor #mass-times so the hero "Mass Times" button scrolls here)
 *   - Consolidated giving: Tithe & Offertory + Project Annex + Mass Intentions
 *   - Announcements strip
 * Removed from homepage: Live Stream block, quick-links row (Groups/Events/Community/Photos),
 * standalone "Our Parish" overview strip (relocated to /about).
 */
const Home = () => {
    const { content } = useContent();
    const H = content.home;
    const CONF = content.confession;

    // Homepage hero slideshow slides, seeded with bundled fallback so the hero
    // always renders even when the backend is unreachable or has no slides yet.
    const [slides, setSlides] = useState(FALLBACK_HERO_SLIDES);
    useEffect(() => {
        let alive = true;
        fetchSlides("home")
            .then((d) => {
                if (!alive || !d?.length) return;
                // Normalize backend rows to the shape HeroSlideshow expects.
                const mapped = d
                    .map((s) => ({ src: s.url || s.src, alt: s.caption || s.alt || "" }))
                    .filter((s) => !!s.src);
                if (mapped.length) setSlides(mapped);
            })
            .catch(() => { /* keep fallback */ });
        return () => { alive = false; };
    }, []);

    return (
        <div>
            <SEO
                title={null}
                description="Holy Trinity Catholic Church Kileleshwa, a Catholic parish community on Othaya Road, Kileleshwa, Nairobi. Mass times, live-streamed liturgies, prayer wall, daily readings and parish life."
            />

            {/* Full-width hero slideshow */}
            <section data-testid={TIDS.home.hero} className="relative overflow-hidden">
                <HeroSlideshow slides={slides} intervalMs={5000} />
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-[5]"
                    style={{ background: "linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.1) 100%)" }}
                />
                <div className="pointer-events-none absolute inset-0 z-10 flex items-center">
                    <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
                        <div className="max-w-3xl">
                            <h1 className="sr-only">Holy Trinity Catholic Church, Kileleshwa, Archdiocese of Nairobi</h1>
                            <h2 className="heading-display text-balance" style={{ color: "#FFFFFF", textShadow: "0 3px 22px rgba(0,0,0,0.85), 0 1px 4px rgba(0,0,0,0.7)" }}>
                                <span
                                    className="block text-2xl sm:text-3xl font-semibold uppercase tracking-[0.18em]"
                                    style={{ color: "#F3C544", textShadow: "0 2px 14px rgba(0,0,0,0.85)" }}
                                >
                                    {H.heroTitleLine1}
                                </span>
                                <span className="mt-3 block">{H.heroTitleLine2}</span>
                            </h2>
                            <p
                                data-testid="hero-motto-extension"
                                className="mt-4 italic text-lg sm:text-xl"
                                style={{ color: "#F3C544", textShadow: "0 2px 14px rgba(0,0,0,0.85)" }}
                            >
                                {H.heroTagline}
                            </p>
                            <p
                                className="mt-4 max-w-2xl text-base sm:text-lg text-pretty"
                                style={{ color: "#FFFFFF", textShadow: "0 2px 14px rgba(0,0,0,0.85)" }}
                            >
                                {H.heroWelcome}
                            </p>
                            <div className="mt-7 flex flex-wrap items-center gap-3 pointer-events-auto">
                                {/* Mass Times = anchor to #mass-times, no separate page */}
                                <a href="#mass-times" data-testid={TIDS.home.ctaJoinMass} className="btn-primary">
                                    <Calendar size={16} /> {H.ctaMassTimes}
                                </a>
                                <Link to="/prayer-wall" data-testid={TIDS.home.ctaPrayerWall} className="btn-ghost">
                                    <Heart size={16} /> {H.ctaPrayerWall}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <DailySpiritualHub />

            {/* Mass Times, subtext under the header (not side-by-side) */}
            <section id="mass-times" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-20 scroll-mt-24">
                <div className="mb-6">
                    <span className="eyebrow">Schedule</span>
                    <h2 className="heading-section mt-1">Mass Times</h2>
                    <p className="mt-3 max-w-3xl text-sm sm:text-base text-maroon-700/85 leading-relaxed">
                        Join us for the Holy Eucharist throughout the week. {CONF.line}
                    </p>
                </div>
                <MassTimes />
            </section>

            <AnnouncementsHomeRow />

            {/* Consolidated giving section, hosts Tithe/Offertory poster + Project Annex poster
                + Mass Intentions call-to-action + phone numbers. Paybill lives inside the posters. */}
            <TitheOffertory />

            <div className="pb-16" />
        </div>
    );
};

export default Home;
